// src/utils/consts.ts
var chainId = Number(import.meta.env?.VITE_CHAIN_ID ?? 31337);
var worldAddress = import.meta.env?.VITE_WORLD_ADDRESS;
var startBlock = BigInt(import.meta.env?.VITE_START_BLOCK ?? 0n);
var shopManagementSystemId = import.meta.env?.VITE_SHOP_MANAGEMENT_SYSTEM_ID;
var shopProgramAddress = import.meta.env?.VITE_SHOP_PROGRAM_ADDRESS;
var claimableShopSystemId = import.meta.env?.VITE_CLAIMABLE_SHOP_SYSTEM_ID;
var claimableShopAddress = import.meta.env?.VITE_CLAIMABLE_SHOP_ADDRESS;
var buildSystemId = import.meta.env?.VITE_BUILD_SYSTEM_ID;
var mineSystemId = import.meta.env?.VITE_MINE_SYSTEM_ID;
var transferSystemId = import.meta.env?.VITE_TRANSFER_SYSTEM_ID;
var pesosSystemId = import.meta.env?.VITE_PESOS_SYSTEM_ID;
var adminSystemId = import.meta.env?.VITE_ADMIN_SYSTEM_ID;

// src/utils/timeAgo.ts
function timeAgo(timestamp) {
  const units = [
    { name: "y", limit: 365 * 24 * 60 * 60, inSeconds: 365 * 24 * 60 * 60 },
    { name: "mth", limit: 30 * 24 * 60 * 60, inSeconds: 30 * 24 * 60 * 60 },
    { name: "d", limit: 24 * 60 * 60, inSeconds: 24 * 60 * 60 },
    { name: "h", limit: 60 * 60, inSeconds: 60 * 60 },
    { name: "m", limit: 60, inSeconds: 60 },
    { name: "s", limit: 1, inSeconds: 1 }
  ];
  const currentTimestampSeconds = Math.floor(Date.now() / 1e3);
  const diff = currentTimestampSeconds - Number(timestamp);
  for (const unit of units) {
    if (diff >= unit.limit) {
      const unitsAgo = Math.floor(diff / unit.inSeconds);
      return `${unitsAgo}${unit.name} ago`;
    }
  }
  return "0s ago";
}

// src/utils/formatData.ts
function formatDate(timestamp) {
  const date = new Date(Number(timestamp) * 1e3);
  return date.toLocaleDateString("en-US", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
}

// src/utils/formatEther.ts
import { formatEther as viemFormatEther } from "viem";
function formatEther(wei, decimals = 0) {
  if (!wei) return "0";
  return Number(viemFormatEther(wei)).toFixed(decimals);
}

// src/utils/formatPrice.ts
function formatPrice({
  price,
  amount,
  decimals = 2
}) {
  if (price === void 0) {
    return "$P 0";
  }
  if (!amount) {
    return `$P ${formatEther(price, decimals)}`;
  }
  return `$P ${formatEther(price, decimals)} (${Number(amount)})`;
}

// src/utils/getObjectImageUrl.ts
import { anvil } from "viem/chains";
function getObjectImageUrl(objectId) {
  if (chainId === anvil.id) {
    return `http://localhost:3000/api/assets/objects/${objectId}/icon`;
  }
  return `https://alpha.dustproject.org/api/assets/objects/${objectId}/icon`;
}

// src/utils/getWorldAddress.ts
function getWorldAddress() {
  if (!worldAddress) {
    throw new Error(
      "No world address configured. Is the world still deploying?"
    );
  }
  return worldAddress;
}

// src/utils/getNamespace.ts
import { anvil as anvil2 } from "viem/chains";
function getNamespace(namespace = "permutation") {
  if (chainId === anvil2.id) {
    return `${worldAddress}__${namespace}__`;
  }
  return namespace ? `${namespace}__` : "";
}

// src/utils/indexerUrl.ts
import { anvil as anvil3, garnet, redstone } from "viem/chains";
var indexerUrlMap = {
  [anvil3.id]: "http://0.0.0.0:13691/q",
  [garnet.id]: "https://indexer.mud.garnetchain.com/q",
  [redstone.id]: "https://indexer.alpha.dustproject.org/q"
};
function indexerUrl() {
  const indexerUrl2 = indexerUrlMap[chainId];
  if (!indexerUrl2) {
    throw new Error(`No indexer URL configured for chain ID ${chainId}.`);
  }
  return indexerUrl2;
}

// src/utils/decodeTransactionError.ts
import { decodeErrorResult, parseEventLogs } from "viem";
import { entryPoint07Abi } from "viem/account-abstraction";
function decodeTransactionError(abi, response) {
  const receipt = response.receipt;
  const encodedReason = parseEventLogs({
    logs: receipt.receipt.logs,
    abi: entryPoint07Abi
  });
  const revertReason = encodedReason.find(
    (log) => log.eventName === "UserOperationRevertReason"
  )?.args.revertReason;
  if (revertReason) {
    const decodedReason = decodeErrorResult({
      data: revertReason,
      abi
    });
    console.log("decodedReason:", decodedReason);
    return `${decodedReason.errorName}: ${decodedReason?.args?.join(", ")}`;
  }
  return null;
}

// src/utils/formatColumnName.ts
import { anvil as anvil4 } from "viem/chains";
function formatColumnName(str) {
  if (chainId !== anvil4.id) {
    return str;
  }
  if (str.includes("_")) {
    return str;
  }
  return str.replace(/([A-Z])/g, "_$1").toLowerCase().replace(/^_/, "");
}

// src/utils/encodeObjectId.ts
import { encodeAbiParameters, keccak256 } from "viem";
function encodeObjectId(objectId) {
  return keccak256(
    encodeAbiParameters([{ type: "uint16" }], [Number(objectId)])
  );
}

// src/utils/objectTypes.ts
import { objects } from "@dust/world/internal";

// src/utils/objectTypesInfo.ts
var objectTypesInfo = [
  {
    name: "bedrock",
    num_blocks: 50031505,
    percentage: "0.78%",
    farmable: "FALSE",
    category: "UNBREAKABLE",
    id: 3
  },
  {
    name: "deepslate",
    num_blocks: 621526306,
    percentage: "9.65%",
    farmable: "FALSE",
    category: "HARD_STONE",
    id: 5
  },
  {
    name: "clay",
    num_blocks: 31571945,
    percentage: "0.49%",
    farmable: "FALSE",
    category: "SOIL",
    id: 36
  },
  {
    name: "deepslate_redstone_ore",
    num_blocks: 2107998,
    percentage: "0.03%",
    farmable: "FALSE",
    category: "MEDIUM_ORES",
    id: null
  },
  {
    name: "tuff",
    num_blocks: 67238290,
    percentage: "1.04%",
    farmable: "FALSE",
    category: "STONE",
    id: 7
  },
  {
    name: "gravel",
    num_blocks: 32824036,
    percentage: "0.51%",
    farmable: "FALSE",
    category: "SOIL",
    id: 31
  },
  {
    name: "deepslate_lapis_ore",
    num_blocks: 925620,
    percentage: "0.01%",
    farmable: "FALSE",
    category: "MEDIUM_ORES",
    id: null
  },
  {
    name: "lava",
    num_blocks: 7444658,
    percentage: "0.12%",
    farmable: "FALSE",
    category: "LAVA",
    id: 111
  },
  {
    name: "deepslate_neptunium_ore",
    num_blocks: 1290045,
    percentage: "0.02%",
    farmable: "FALSE",
    category: "PRECIOUS_ORES",
    id: null
  },
  {
    name: "deepslate_iron_ore",
    num_blocks: 1941845,
    percentage: "0.03%",
    farmable: "FALSE",
    category: "COMMON_ORES",
    id: null
  },
  {
    name: "deepslate_gold_ore",
    num_blocks: 1358024,
    percentage: "0.02%",
    farmable: "FALSE",
    category: "MEDIUM_ORES",
    id: null
  },
  {
    name: "moss_block",
    num_blocks: 6726302,
    percentage: "0.10%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 23
  },
  {
    name: "switch_grass",
    num_blocks: 2371966,
    percentage: "0.04%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 83
  },
  {
    name: "air",
    num_blocks: 3857392694,
    percentage: "59.87%",
    farmable: "FALSE",
    category: "N/A",
    id: 1
  },
  {
    name: "fescue_grass",
    num_blocks: 474981,
    percentage: "0.01%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 82
  },
  {
    name: "oak_leaf",
    num_blocks: 2595240,
    percentage: "0.04%",
    farmable: "TRUE",
    category: "VEGETATION",
    id: 58
  },
  {
    name: "water",
    num_blocks: 106636779,
    percentage: "1.66%",
    farmable: "FALSE",
    category: "WATER",
    id: 2
  },
  {
    name: "smooth_basalt",
    num_blocks: 11471444,
    percentage: "0.18%",
    farmable: "FALSE",
    category: "HARD_STONE",
    id: 10
  },
  {
    name: "calcite",
    num_blocks: 6869364,
    percentage: "0.11%",
    farmable: "FALSE",
    category: "HARD_STONE",
    id: 8
  },
  {
    name: "amethyst_block",
    num_blocks: 986002,
    percentage: "0.02%",
    farmable: "FALSE",
    category: "HARD_STONE",
    id: 19
  },
  {
    name: "ivy_vine",
    num_blocks: 1281444,
    percentage: "0.02%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 85
  },
  {
    name: "blackstone",
    num_blocks: 263777306,
    percentage: "4.09%",
    farmable: "FALSE",
    category: "HARD_STONE",
    id: 17
  },
  {
    name: "vines_bush",
    num_blocks: 3423515,
    percentage: "0.05%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 84
  },
  {
    name: "obsidian",
    num_blocks: 4541162,
    percentage: "0.07%",
    farmable: "FALSE",
    category: "EXTREMELY_HARD",
    id: 15
  },
  {
    name: "deepslate_copper_ore",
    num_blocks: 545682,
    percentage: "0.01%",
    farmable: "FALSE",
    category: "COMMON_ORES",
    id: null
  },
  {
    name: "copper_ore",
    num_blocks: 7661934,
    percentage: "0.12%",
    farmable: "FALSE",
    category: "COMMON_ORES",
    id: 115
  },
  {
    name: "stone",
    num_blocks: 910160226,
    percentage: "14.13%",
    farmable: "FALSE",
    category: "STONE",
    id: 4
  },
  {
    name: "granite",
    num_blocks: 125996097,
    percentage: "1.96%",
    farmable: "FALSE",
    category: "STONE",
    id: 6
  },
  {
    name: "iron_ore",
    num_blocks: 4213327,
    percentage: "0.07%",
    farmable: "FALSE",
    category: "COMMON_ORES",
    id: 116
  },
  {
    name: "diorite",
    num_blocks: 54694811,
    percentage: "0.85%",
    farmable: "FALSE",
    category: "STONE",
    id: 12
  },
  {
    name: "andesite",
    num_blocks: 104724033,
    percentage: "1.63%",
    farmable: "FALSE",
    category: "STONE",
    id: 11
  },
  {
    name: "dirt",
    num_blocks: 43954807,
    percentage: "0.68%",
    farmable: "FALSE",
    category: "SOIL",
    id: 22
  },
  {
    name: "lapis_ore",
    num_blocks: 629009,
    percentage: "0.01%",
    farmable: "FALSE",
    category: "MEDIUM_ORES",
    id: null
  },
  {
    name: "redstone_ore",
    num_blocks: 148338,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "MEDIUM_ORES",
    id: null
  },
  {
    name: "gold_ore",
    num_blocks: 356434,
    percentage: "0.01%",
    farmable: "FALSE",
    category: "MEDIUM_ORES",
    id: 117
  },
  {
    name: "coal_ore",
    num_blocks: 13116437,
    percentage: "0.20%",
    farmable: "FALSE",
    category: "COMMON_ORES",
    id: 114
  },
  {
    name: "algae",
    num_blocks: 1654570,
    percentage: "0.03%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 103
  },
  {
    name: "raw_copper_block",
    num_blocks: 4681,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "HARD_STONE",
    id: null
  },
  {
    name: "neptunium_ore",
    num_blocks: 18696,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "PRECIOUS_ORES",
    id: 119
  },
  {
    name: "deepslate_coal_ore",
    num_blocks: 813519,
    percentage: "0.01%",
    farmable: "FALSE",
    category: "COMMON_ORES",
    id: null
  },
  {
    name: "mossy_cobblestone",
    num_blocks: 242769,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 14
  },
  {
    name: "cobblestone",
    num_blocks: 3724342,
    percentage: "0.06%",
    farmable: "FALSE",
    category: "STONE",
    id: 13
  },
  {
    name: "glowstone",
    num_blocks: 1323,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 20
  },
  {
    name: "raw_iron_block",
    num_blocks: 6361,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "HARD_STONE",
    id: null
  },
  {
    name: "mud",
    num_blocks: 2646207,
    percentage: "0.04%",
    farmable: "FALSE",
    category: "SOIL",
    id: 26
  },
  {
    name: "red_mushroom",
    num_blocks: 15635,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 88
  },
  {
    name: "hemp_bush",
    num_blocks: 393678,
    percentage: "0.01%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 86
  },
  {
    name: "golden_mushroom",
    num_blocks: 18588,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 87
  },
  {
    name: "bamboo_bush",
    num_blocks: 482649,
    percentage: "0.01%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 99
  },
  {
    name: "muddy_mangrove_roots",
    num_blocks: 118614,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "WATER",
    id: 69
  },
  {
    name: "coffee_bush",
    num_blocks: 2928,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 89
  },
  {
    name: "fly_trap",
    num_blocks: 1650,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 81
  },
  {
    name: "dripstone_block",
    num_blocks: 4076835,
    percentage: "0.06%",
    farmable: "FALSE",
    category: "HARD_STONE",
    id: 16
  },
  {
    name: "bone_block",
    num_blocks: 2010,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 113
  },
  {
    name: "azalea_leaf",
    num_blocks: 356513,
    percentage: "0.01%",
    farmable: "TRUE",
    category: "VEGETATION",
    id: 65
  },
  {
    name: "flowering_azalea_leaf",
    num_blocks: 178592,
    percentage: "0.00%",
    farmable: "TRUE",
    category: "VEGETATION",
    id: 66
  },
  {
    name: "jungle_leaf",
    num_blocks: 2324437,
    percentage: "0.04%",
    farmable: "TRUE",
    category: "VEGETATION",
    id: 60
  },
  {
    name: "brown_mushroom_block",
    num_blocks: 81769,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "DENSE_MATERIALS",
    id: 97
  },
  {
    name: "mushroom_stem",
    num_blocks: 28389,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "DENSE_MATERIALS",
    id: 98
  },
  {
    name: "diamond_ore",
    num_blocks: 23046,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "PRECIOUS_ORES",
    id: 118
  },
  {
    name: "sakura_leaf",
    num_blocks: 271541,
    percentage: "0.00%",
    farmable: "TRUE",
    category: "VEGETATION",
    id: 61
  },
  {
    name: "ultraviolet",
    num_blocks: 3361,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 79
  },
  {
    name: "mangrove_log",
    num_blocks: 127718,
    percentage: "0.00%",
    farmable: "TRUE",
    category: "DENSE_MATERIALS",
    id: 57
  },
  {
    name: "fire_flower",
    num_blocks: 9538,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 76
  },
  {
    name: "sakura_log",
    num_blocks: 20273,
    percentage: "0.00%",
    farmable: "TRUE",
    category: "DENSE_MATERIALS",
    id: 53
  },
  {
    name: "terracotta",
    num_blocks: 14144256,
    percentage: "0.22%",
    farmable: "FALSE",
    category: "STONE",
    id: 37
  },
  {
    name: "spider_web",
    num_blocks: 30387,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 112
  },
  {
    name: "oak_log",
    num_blocks: 154078,
    percentage: "0.00%",
    farmable: "TRUE",
    category: "DENSE_MATERIALS",
    id: 50
  },
  {
    name: "grass_block",
    num_blocks: 7577173,
    percentage: "0.12%",
    farmable: "FALSE",
    category: "SOIL",
    id: 21
  },
  {
    name: "sand",
    num_blocks: 1699701,
    percentage: "0.03%",
    farmable: "FALSE",
    category: "SOIL",
    id: 32
  },
  {
    name: "cobbled_deepslate",
    num_blocks: 630513,
    percentage: "0.01%",
    farmable: "FALSE",
    category: "HARD_STONE",
    id: 18
  },
  {
    name: "ice",
    num_blocks: 3640707,
    percentage: "0.06%",
    farmable: "FALSE",
    category: "WATER",
    id: 110
  },
  {
    name: "sandstone",
    num_blocks: 1643035,
    percentage: "0.03%",
    farmable: "FALSE",
    category: "STONE",
    id: 34
  },
  {
    name: "cotton_bush",
    num_blocks: 64870,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 93
  },
  {
    name: "red_mushroom_block",
    num_blocks: 76129,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "DENSE_MATERIALS",
    id: 96
  },
  {
    name: "deepslate_diamond_ore",
    num_blocks: 3903,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "PRECIOUS_ORES",
    id: null
  },
  {
    name: "dark_oak_leaf",
    num_blocks: 1290819,
    percentage: "0.02%",
    farmable: "TRUE",
    category: "VEGETATION",
    id: 64
  },
  {
    name: "dark_oak_log",
    num_blocks: 218836,
    percentage: "0.00%",
    farmable: "TRUE",
    category: "DENSE_MATERIALS",
    id: 56
  },
  {
    name: "dandelion_flower",
    num_blocks: 5941,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 72
  },
  {
    name: "morningglory_flower",
    num_blocks: 2883,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 77
  },
  {
    name: "rose_flower",
    num_blocks: 3150,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 75
  },
  {
    name: "daylily_flower",
    num_blocks: 3783,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 73
  },
  {
    name: "birch_leaf",
    num_blocks: 463539,
    percentage: "0.01%",
    farmable: "TRUE",
    category: "VEGETATION",
    id: 59
  },
  {
    name: "azalea_flower",
    num_blocks: 2524,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 70
  },
  {
    name: "birch_log",
    num_blocks: 65946,
    percentage: "0.00%",
    farmable: "TRUE",
    category: "DENSE_MATERIALS",
    id: 51
  },
  {
    name: "strawberry_bush",
    num_blocks: 3842,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 90
  },
  {
    name: "peony_flower",
    num_blocks: 3714,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 78
  },
  {
    name: "farmland",
    num_blocks: 21,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "SOIL",
    id: 28
  },
  {
    name: "wheat",
    num_blocks: 21,
    percentage: "0.00%",
    farmable: "TRUE",
    category: "VEGETATION",
    id: 92
  },
  {
    name: "podzol",
    num_blocks: 246487,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "SOIL",
    id: 24
  },
  {
    name: "red_sand",
    num_blocks: 310860,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "SOIL",
    id: 33
  },
  {
    name: "spruce_leaf",
    num_blocks: 1841838,
    percentage: "0.03%",
    farmable: "TRUE",
    category: "VEGETATION",
    id: 62
  },
  {
    name: "spruce_log",
    num_blocks: 268854,
    percentage: "0.00%",
    farmable: "TRUE",
    category: "DENSE_MATERIALS",
    id: 55
  },
  {
    name: "yellow_terracotta",
    num_blocks: 2239086,
    percentage: "0.03%",
    farmable: "FALSE",
    category: "STONE",
    id: 42
  },
  {
    name: "red_terracotta",
    num_blocks: 1745325,
    percentage: "0.03%",
    farmable: "FALSE",
    category: "STONE",
    id: 43
  },
  {
    name: "white_terracotta",
    num_blocks: 11868569,
    percentage: "0.18%",
    farmable: "FALSE",
    category: "STONE",
    id: 40
  },
  {
    name: "orange_terracotta",
    num_blocks: 2057166,
    percentage: "0.03%",
    farmable: "FALSE",
    category: "STONE",
    id: 39
  },
  {
    name: "red_sandstone",
    num_blocks: 101814,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 35
  },
  {
    name: "light_gray_terracotta",
    num_blocks: 806628,
    percentage: "0.01%",
    farmable: "FALSE",
    category: "STONE",
    id: 41
  },
  {
    name: "brown_terracotta",
    num_blocks: 3218340,
    percentage: "0.05%",
    farmable: "FALSE",
    category: "STONE",
    id: 38
  },
  {
    name: "acacia_leaf",
    num_blocks: 1035091,
    percentage: "0.02%",
    farmable: "TRUE",
    category: "VEGETATION",
    id: 63
  },
  {
    name: "acacia_log",
    num_blocks: 219214,
    percentage: "0.00%",
    farmable: "TRUE",
    category: "DENSE_MATERIALS",
    id: 54
  },
  {
    name: "cactus",
    num_blocks: 80,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "PLANTS",
    id: 100
  },
  {
    name: "light_blue_terracotta",
    num_blocks: 219142,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 44
  },
  {
    name: "cyan_terracotta",
    num_blocks: 139274,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 45
  },
  {
    name: "purple_terracotta",
    num_blocks: 101296,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 47
  },
  {
    name: "blue_terracotta",
    num_blocks: 122228,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 48
  },
  {
    name: "magenta_terracotta",
    num_blocks: 29844,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 49
  },
  {
    name: "jungle_log",
    num_blocks: 521177,
    percentage: "0.01%",
    farmable: "TRUE",
    category: "DENSE_MATERIALS",
    id: 52
  },
  {
    name: "basalt",
    num_blocks: 9019090,
    percentage: "0.14%",
    farmable: "FALSE",
    category: "HARD_STONE",
    id: 9
  },
  {
    name: "melon",
    num_blocks: 4887,
    percentage: "0.00%",
    farmable: "TRUE",
    category: "PLANTS",
    id: 95
  },
  {
    name: "pumpkin",
    num_blocks: 920,
    percentage: "0.00%",
    farmable: "TRUE",
    category: "PLANTS",
    id: 94
  },
  {
    name: "mangrove_leaf",
    num_blocks: 1901703,
    percentage: "0.03%",
    farmable: "TRUE",
    category: "VEGETATION",
    id: 67
  },
  {
    name: "mangrove_roots",
    num_blocks: 201694,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "WATER",
    id: 68
  },
  {
    name: "packed_mud",
    num_blocks: 79582,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "SOIL",
    id: 27
  },
  {
    name: "snow",
    num_blocks: 1735419,
    percentage: "0.03%",
    farmable: "FALSE",
    category: "WATER",
    id: 109
  },
  {
    name: "raspberry_bush",
    num_blocks: 882,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 91
  },
  {
    name: "dirt_path",
    num_blocks: 13608,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "SOIL",
    id: 25
  },
  {
    name: "lilac_flower",
    num_blocks: 1314,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 74
  },
  {
    name: "bell_flower",
    num_blocks: 328,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 71
  },
  {
    name: "tube_coral_block",
    num_blocks: 1401,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 106
  },
  {
    name: "fire_coral_block",
    num_blocks: 1322,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 105
  },
  {
    name: "brain_coral_block",
    num_blocks: 1297,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 108
  },
  {
    name: "coral",
    num_blocks: 809,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "WATER",
    id: 101
  },
  {
    name: "sea_anemone",
    num_blocks: 243,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "WATER",
    id: 102
  },
  {
    name: "bubble_coral_block",
    num_blocks: 1347,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 107
  },
  {
    name: "horn_coral_block",
    num_blocks: 1018,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 104
  },
  {
    name: "sun_flower",
    num_blocks: 86,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "VEGETATION",
    id: 80
  },
  {
    name: "black_terracotta",
    num_blocks: 2072,
    percentage: "0.00%",
    farmable: "FALSE",
    category: "STONE",
    id: 46
  }
];

// src/utils/objectTypes.ts
var nonTradeableObjectTypes = [
  0,
  1,
  2,
  32794,
  32795,
  32796,
  32797,
  32798,
  32799
];
var objectTypes = objects.reduce(
  (acc, item) => {
    acc[item.id] = {
      ...item,
      maxSupply: objectTypesInfo.find((type) => type.id === item.id)?.num_blocks
    };
    return acc;
  },
  {}
);
var defaultObjectTypeId = 50;
var defaultObjectType = objectTypes[defaultObjectTypeId];
var tradeableObjectTypesIds = Object.keys(objectTypes).filter(
  (key) => {
    const objectType = objectTypes[Number(key)];
    return !nonTradeableObjectTypes.includes(objectType.id);
  }
);
var tradeableObjectTypes = Object.fromEntries(
  tradeableObjectTypesIds.map((id) => [id, objectTypes[Number(id)]])
);
var encodedObjectsMap = objects.map((object) => {
  return {
    id: object.id,
    encodedId: encodeObjectId(object.id.toString())
  };
}).reduce((acc, object) => {
  acc[object.encodedId] = object.id;
  return acc;
}, {});

// src/utils/camelToSpaces.ts
function camelToSpaces(str) {
  return str.replace(/([A-Z])/g, " $1").replace(/^./, (str2) => str2.toUpperCase()).trim();
}

// src/utils/decodeBlueprint.ts
var CHUNK_SIZE = 16;
var VERSION_PADDING = 1;
var OFFSET_PADDING = 2;
function fromChunkCoord(chunkCoord, relativeCoord) {
  return {
    x: chunkCoord.x * CHUNK_SIZE + relativeCoord.x,
    y: chunkCoord.y * CHUNK_SIZE + relativeCoord.y,
    z: chunkCoord.z * CHUNK_SIZE + relativeCoord.z
  };
}
function decodeBlueprint(chunkCoord, data) {
  const blocks = [];
  if (data.length < VERSION_PADDING + OFFSET_PADDING) {
    return blocks;
  }
  const version = data[0];
  const offset = data[1] << 8 | data[2];
  console.log({ version, offset });
  const SUPPORTED_VERSION = 0;
  if (version !== SUPPORTED_VERSION) {
    throw new Error(
      `Unsupported blueprint encoding version: 0x${version.toString(16).padStart(2, "0")}`
    );
  }
  const headerSize = VERSION_PADDING + OFFSET_PADDING;
  const voxelData = data.slice(headerSize);
  for (let x = 0; x < CHUNK_SIZE; x++) {
    for (let y = 0; y < CHUNK_SIZE; y++) {
      for (let z = 0; z < CHUNK_SIZE; z++) {
        const originalIndex = 3 * (x * CHUNK_SIZE * CHUNK_SIZE + y * CHUNK_SIZE + z);
        const adjustedIndex = originalIndex - offset;
        if (adjustedIndex >= 0 && adjustedIndex + 2 < voxelData.length) {
          const id = voxelData[adjustedIndex] << 8 | voxelData[adjustedIndex + 1];
          const orientation = voxelData[adjustedIndex + 2];
          if (id !== 0) {
            const worldCoord = fromChunkCoord(chunkCoord, { x, y, z });
            blocks.push({
              coord: worldCoord,
              id,
              orientation
            });
          }
        }
      }
    }
  }
  return blocks;
}

// src/utils/truncateStr.ts
function truncateStr(str, maxLength = 10) {
  if (str.length <= maxLength) {
    return str;
  }
  return `${str.slice(0, maxLength)}\u2026`;
}

// src/utils/toNumber.ts
function toNumber(input) {
  if (typeof input !== "string") return Number.NaN;
  const cleaned = input.replace(/,/g, "").trim();
  const num = Number(cleaned);
  return Number.isNaN(num) ? Number.NaN : num;
}
export {
  adminSystemId,
  buildSystemId,
  camelToSpaces,
  chainId,
  claimableShopAddress,
  claimableShopSystemId,
  decodeBlueprint,
  decodeTransactionError,
  defaultObjectType,
  defaultObjectTypeId,
  encodeObjectId,
  encodedObjectsMap,
  formatColumnName,
  formatDate,
  formatEther,
  formatPrice,
  getNamespace,
  getObjectImageUrl,
  getWorldAddress,
  indexerUrl,
  mineSystemId,
  nonTradeableObjectTypes,
  objectTypes,
  pesosSystemId,
  shopManagementSystemId,
  shopProgramAddress,
  startBlock,
  timeAgo,
  toNumber,
  tradeableObjectTypes,
  tradeableObjectTypesIds,
  transferSystemId,
  truncateStr,
  worldAddress
};
//# sourceMappingURL=index.mjs.map